import React, { useState } from 'react';

import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Typography,
  Upload,
} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

const FormField = ({ field, disabled, readOnly, icdCodeNames, meData }) => {
  const [otherSelected, setOtherSelected] = useState(false);

  const { hidden, optionsFrom, dateValidation, allowClear, ...formFields } = field;

  if (hidden) {
    if (typeof hidden === 'function') {
      if (hidden(meData)) {
        return null;
      }
    } else {
      return null;
    }
  }

  const getOptions = () => {
    if (optionsFrom === 'icdCodeNames') {
      return icdCodeNames.map((code) => ({
        value: code,
        label: code,
      }));
    }
    return field.options || [];
  };

  const renderField = () => {
    switch (field.type) {
      case 'checkbox':
        return (
          <Checkbox disabled={disabled} readOnly={readOnly} required={field.required}>
            {field.label}
          </Checkbox>
        );

      case 'typography':
        return (
          <div key={field.title} className={field.className}>
            <Typography.Title className level={5}>
              {field.title}
            </Typography.Title>
            <Typography.Text>{field.content}</Typography.Text>
          </div>
        );

      case 'select':
        return (
          <Select
            placeholder={field.placeholder}
            disabled={disabled}
            open={readOnly ? false : undefined}
            allowClear={allowClear}
            style={{ width: '100%', ...field.style }}
            options={getOptions()}
          />
        );

      case 'datePicker':
        return (
          <DatePicker
            style={field.style}
            required={field.required}
            readOnly={readOnly}
            disabled={disabled || field.disabled}
            defaultValue={field?.defaultValue}
            format="MM-DD-YYYY"
            disabledDate={(current) => {
              if (dateValidation) {
                // Don't allow future dates if specified
                if (dateValidation.disallowFuture && current > dayjs().endOf('day')) {
                  return true;
                }
                // Check for minimum age in days
                if (
                  dateValidation.minAge &&
                  current > dayjs().subtract(dateValidation.minAge, 'days').endOf('day')
                ) {
                  return true;
                }
                // Check for maximum age in days
                if (
                  dateValidation.maxAge &&
                  current < dayjs().subtract(dateValidation.maxAge, 'days').startOf('day')
                ) {
                  return true;
                }
              }
              return false;
            }}
            {...formFields}
          />
        );

      case 'number':
        return (
          <InputNumber
            min={field.min}
            max={field.max}
            suffix={field.suffix}
            prefix={field.prefix}
            required={field.required}
            className={field.className}
            placeholder={field.placeholder}
            disabled={disabled}
            readOnly={readOnly}
          />
        );

      case 'otp':
        return (
          <Input.OTP
            length={field.length}
            required={field.required}
            disabled={disabled}
            readOnly={readOnly}
          />
        );
      case 'textarea':
        return (
          <Input.TextArea
            placeholder={field.placeholder}
            required={field.required}
            disabled={field.disabled || disabled}
            readOnly={readOnly}
            rows={field.rows || 3}
            className="!resize-none"
          />
        );
      case 'radio-group':
        return (
          <Radio.Group
            disabled={field.disabled || disabled}
            readOnly={readOnly}
            onChange={(e) => {
              if (field.options.some((opt) => opt.value === 'other')) {
                setOtherSelected(e.target.value === 'other');
              }
            }}
          >
            {field.options.map((opt) => (
              <Radio key={opt.value} value={opt.value} disabled={opt.disabled || disabled}>
                {opt.label}
              </Radio>
            ))}
          </Radio.Group>
        );
      case 'checkbox-group':
        return (
          <Checkbox.Group
            options={field.options.map((opt) => ({
              label: opt.label,
              value: opt.value,
              disabled: opt.disabled || disabled,
            }))}
            readOnly={readOnly}
            disabled={field.disabled || disabled}
            defaultValue={field.options.filter((opt) => opt.defaultChecked).map((opt) => opt.value)}
          />
        );
      case 'description':
        return (
          <Descriptions.Item label={field.label}>
            <Form.Item name={field.name} className="mb-0" initialValue={field.defaultValue}>
              {field.inputType === 'datePicker' ? (
                <DatePicker
                  disabled={disabled || field.disabled}
                  defaultValue={dayjs(new Date())}
                  style={field.style}
                />
              ) : (
                <Input
                  disabled={disabled || field.disabled}
                  defaultValue={field.defaultValue}
                  style={field.style}
                />
              )}
            </Form.Item>
          </Descriptions.Item>
        );
      case 'upload':
        return (
          <Upload
            readOnly={readOnly}
            disabled={disabled}
            maxCount={1}
            beforeUpload={(file) => {
              return false; // Prevent auto upload
            }}
            {...formFields}
          >
            <Button>Upload File</Button>
          </Upload>
        );
      case 'input':
        return (
          <Input
            defaultValue={field.defaultValue}
            readOnly={readOnly}
            suffix={field.suffix}
            prefix={field.prefix}
            placeholder={field.placeholder}
            required={field.required}
            disabled={field.disabled || disabled}
          />
        );
      default:
        return (
          <Input
            defaultValue={field.defaultValue}
            readOnly={readOnly}
            suffix={field.suffix}
            prefix={field.prefix}
            placeholder={field.placeholder}
            required={field.required}
            disabled={field.disabled || disabled}
          />
        );
    }
  };

  const getFieldProps = () => {
    const baseProps = {
      ...formFields,
      name: field.name,
      label: field.type !== 'checkbox' && field.label,
      extra: field.extra,
    };

    // Add validation rules if required
    if (field.required) {
      baseProps.rules = [
        {
          required: true,
          message:
            field.type === 'upload'
              ? `Please upload ${field.label}`
              : field.type === 'select' || field.type === 'datePicker'
                ? `Please select the ${field.label}`
                : `Please enter the ${field.label}`,
        },
      ];

      // Add additional validation for datePicker
      if (field.type === 'datePicker') {
        baseProps.rules.push({
          validator: (_, value) => {
            if (!value) return Promise.resolve();

            if (field.dateValidation) {
              // Don't allow future dates if specified
              if (field.dateValidation.disallowFuture && value > dayjs()) {
                return Promise.reject(
                  field.dateValidation.futureMessage || 'Date cannot be in the future'
                );
              }
              // Check for minimum age in days
              if (
                field.dateValidation.minAge &&
                value > dayjs().subtract(field.dateValidation.minAge, 'days')
              ) {
                return Promise.reject(
                  field.dateValidation.minAgeMessage ||
                    `Date must be at least ${field.dateValidation.minAge} days old`
                );
              }

              if (
                field.dateValidation.maxAge &&
                value < dayjs().subtract(field.dateValidation.maxAge, 'days')
              ) {
                return Promise.reject(
                  field.dateValidation.maxAgeMessage ||
                    `Date cannot be more than ${field.dateValidation.maxAge} days old`
                );
              }
            }

            return Promise.resolve();
          },
        });
      }
    }

    // Add specific props based on field type
    switch (field.type) {
      case 'checkbox':
        return {
          ...baseProps,
          valuePropName: 'checked',
          initialValue: false,
        };
      case 'checkbox-group':
        return {
          ...baseProps,
          initialValue: field.options.filter((opt) => opt.defaultChecked).map((opt) => opt.value),
        };
      case 'number':
        return {
          ...baseProps,
          valuePropName: 'value',
          initialValue: null,
        };
      case 'datePicker':
        return {
          ...baseProps,
          valuePropName: 'value',
          initialValue: field.defaultValue || null,
        };
      case 'upload':
        return {
          ...baseProps,
          valuePropName: 'fileList',
          getValueFromEvent: (e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return e?.fileList;
          },
          initialValue: [],
        };
      case 'select':
        return {
          ...baseProps,
          initialValue: undefined, // This ensures placeholder is shown
        };
      case 'radio-group':
        return {
          ...baseProps,
          valuePropName: 'value',
        };
      default:
        return {
          ...baseProps,
          valuePropName: 'value',
          initialValue: field.defaultValue || field.value || '',
        };
    }
  };

  if (field.type === 'description') {
    return renderField();
  }

  return (
    <Col
      span={field.span}
      className="form-section"
      style={{ display: field.invisible ? 'none' : 'block' }}
    >
      {field.type === 'typography' ? (
        <div key={field.title} className="mb-4 mt-2">
          <Typography.Title className="font-bold" level={5}>
            {field.title}
          </Typography.Title>
          <Typography.Text>{field.content}</Typography.Text>
        </div>
      ) : (
        <Form.Item
          data-testid={`field-${field.name}`}
          style={{
            pageBreakInside: 'avoid',
            breakInside: 'avoid',
          }}
          {...getFieldProps()}
        >
          {renderField()}
        </Form.Item>
      )}
    </Col>
  );
};

FormField.propTypes = {
  field: PropTypes.object.isRequired,
  isRefill: PropTypes.bool,
  icdCodeNames: PropTypes.array,
  meData: PropTypes.object,
};

export default FormField;
